/**
 * Text Canvas - ORP Layout and Font Fitting for XR Reading Pane
 *
 * Creates and manages a canvas for rendering RSVP words with:
 * - ORP (Optimal Recognition Point) anchoring at 45% of panel width
 * - Font sizing: 5-8 letter words fill 60-75% of usable width
 * - Long word scaling with minimum clamp (no wrapping)
 * - Font: Inter SemiBold (600)
 * - Updates only when word changes
 *
 * All sizing parameters come from CSS tokens (--xr-reading-*).
 */

import { getWordLetterCount } from '../../core/tokenization';

/**
 * Configuration from CSS tokens
 */
export interface TextCanvasConfig {
  /** Texture width in pixels */
  textureWidth: number;
  /** Texture height in pixels */
  textureHeight: number;
  /** ORP anchor as fraction of width (0-1) */
  orpX: number;
  /** Horizontal padding as fraction of width */
  paddingX: number;
  /** Vertical padding as fraction of height */
  paddingY: number;
  /** Minimum font size in pixels */
  fontSizeMin: number;
  /** Maximum font size in pixels */
  fontSizeMax: number;
  /** Target fill range for typical words (min fraction of usable width) */
  wordFillMin: number;
  /** Target fill range for typical words (max fraction of usable width) */
  wordFillMax: number;
  /** Typical word length range (min letters) */
  typicalWordLenMin: number;
  /** Typical word length range (max letters) */
  typicalWordLenMax: number;
  /** Long word threshold (letter count) */
  longWordThreshold: number;
  /** Minimum scale for long words */
  longWordScaleMin: number;
  /** Font family */
  fontFamily: string;
  /** Font weight */
  fontWeight: number;
  /** Glass panel fill color */
  glassFill: string;
  /** Glass panel border color */
  glassBorder: string;
  /** Corner radius as fraction of height */
  cornerRadius: number;
}

/**
 * Default configuration (matches tokens.css defaults)
 */
const DEFAULT_CONFIG: TextCanvasConfig = {
  textureWidth: 2048,
  textureHeight: 700,
  orpX: 0.45,
  paddingX: 0.07,
  paddingY: 0.18,
  fontSizeMin: 64,
  fontSizeMax: 160,
  wordFillMin: 0.6,
  wordFillMax: 0.75,
  typicalWordLenMin: 5,
  typicalWordLenMax: 8,
  longWordThreshold: 12,
  longWordScaleMin: 0.65,
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  fontWeight: 600,
  glassFill: 'rgba(255, 255, 255, 0.1)',
  glassBorder: 'rgba(255, 255, 255, 0.2)',
  cornerRadius: 0.08, // 8% of height
};

/**
 * Read a CSS custom property value as a number
 */
function getCSSNumber(name: string, fallback: number): number {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!value) return fallback;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Read a CSS custom property value as a string
 */
function getCSSString(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * Load configuration from CSS tokens
 */
export function loadConfigFromTokens(): TextCanvasConfig {
  return {
    textureWidth: getCSSNumber('--xr-reading-texture-width-px', DEFAULT_CONFIG.textureWidth),
    textureHeight: getCSSNumber('--xr-reading-texture-height-px', DEFAULT_CONFIG.textureHeight),
    orpX: getCSSNumber('--xr-reading-pane-orp-x', DEFAULT_CONFIG.orpX),
    paddingX: getCSSNumber('--xr-reading-pane-padding-x', DEFAULT_CONFIG.paddingX),
    paddingY: getCSSNumber('--xr-reading-pane-padding-y', DEFAULT_CONFIG.paddingY),
    fontSizeMin: getCSSNumber('--xr-reading-font-size-min-px', DEFAULT_CONFIG.fontSizeMin),
    fontSizeMax: getCSSNumber('--xr-reading-font-size-max-px', DEFAULT_CONFIG.fontSizeMax),
    wordFillMin: getCSSNumber('--xr-reading-word-fill-min', DEFAULT_CONFIG.wordFillMin),
    wordFillMax: getCSSNumber('--xr-reading-word-fill-max', DEFAULT_CONFIG.wordFillMax),
    typicalWordLenMin: getCSSNumber('--xr-reading-typical-word-len-min', DEFAULT_CONFIG.typicalWordLenMin),
    typicalWordLenMax: getCSSNumber('--xr-reading-typical-word-len-max', DEFAULT_CONFIG.typicalWordLenMax),
    longWordThreshold: getCSSNumber('--xr-reading-long-word-threshold', DEFAULT_CONFIG.longWordThreshold),
    longWordScaleMin: getCSSNumber('--xr-reading-long-word-scale-min', DEFAULT_CONFIG.longWordScaleMin),
    fontFamily: getCSSString('--xr-reading-font-family', DEFAULT_CONFIG.fontFamily),
    fontWeight: getCSSNumber('--xr-reading-font-weight', DEFAULT_CONFIG.fontWeight),
    glassFill: getCSSString('--xr-ui-panel-fill', DEFAULT_CONFIG.glassFill),
    glassBorder: getCSSString('--xr-ui-panel-border', DEFAULT_CONFIG.glassBorder),
    cornerRadius: DEFAULT_CONFIG.cornerRadius,
  };
}

/**
 * Get default configuration (for testing)
 */
export function getDefaultConfig(): TextCanvasConfig {
  return { ...DEFAULT_CONFIG };
}

/**
 * Calculate base font size for typical words (pure function for testing)
 *
 * @param config - TextCanvas configuration
 * @returns Calculated base font size in pixels
 */
export function calculateBaseFontSize(config: TextCanvasConfig): number {
  const usableWidth = config.textureWidth * (1 - 2 * config.paddingX);

  // Target: average typical word fills ~67.5% (midpoint of 60-75%)
  const targetFill = (config.wordFillMin + config.wordFillMax) / 2;
  const targetWidth = usableWidth * targetFill;

  // Average typical word length
  const avgTypicalLen = (config.typicalWordLenMin + config.typicalWordLenMax) / 2;

  // Estimate character width as ~0.6 of font size for Inter SemiBold
  const charWidthRatio = 0.6;

  // font size = targetWidth / (avgTypicalLen * charWidthRatio)
  const estimated = targetWidth / (avgTypicalLen * charWidthRatio);

  // Clamp to configured range
  return Math.max(config.fontSizeMin, Math.min(config.fontSizeMax, estimated));
}

/**
 * Calculate font size for a specific word (pure function for testing)
 *
 * @param word - The word to calculate font size for
 * @param baseFontSize - The base font size for typical words
 * @param config - TextCanvas configuration
 * @returns Calculated font size in pixels
 */
export function calculateFontSizeForWord(
  word: string,
  baseFontSize: number,
  config: TextCanvasConfig
): number {
  const letterCount = getWordLetterCount(word);
  if (letterCount === 0) return baseFontSize;

  // Start with base font size
  let fontSize = baseFontSize;

  // For long words, scale down
  if (letterCount >= config.longWordThreshold) {
    // Scale factor based on how much longer than threshold
    const ratio = config.longWordThreshold / letterCount;
    const scaledSize = baseFontSize * ratio;

    // Apply minimum scale clamp
    const minSize = baseFontSize * config.longWordScaleMin;
    fontSize = Math.max(minSize, scaledSize);
  }

  // Clamp to absolute min/max
  return Math.max(config.fontSizeMin, Math.min(config.fontSizeMax, fontSize));
}

/**
 * Text Canvas for XR RSVP word rendering
 */
export class TextCanvas {
  private canvas: HTMLCanvasElement | OffscreenCanvas;
  private ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  private config: TextCanvasConfig;
  private baseFontSize: number;
  private lastWord: string = '';

  /**
   * Create a new TextCanvas
   *
   * @param config - Configuration (defaults to CSS tokens)
   */
  constructor(config?: Partial<TextCanvasConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Calculate base font size for typical words
    this.baseFontSize = calculateBaseFontSize(this.config);

    // Create canvas
    if (typeof OffscreenCanvas !== 'undefined') {
      this.canvas = new OffscreenCanvas(this.config.textureWidth, this.config.textureHeight);
    } else {
      this.canvas = document.createElement('canvas');
      this.canvas.width = this.config.textureWidth;
      this.canvas.height = this.config.textureHeight;
    }

    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context');
    }
    this.ctx = ctx;

    // Initial clear
    this.clear();
  }

  /**
   * Calculate font size for a specific word (delegates to pure function)
   */
  private getFontSizeForWord(word: string): number {
    return calculateFontSizeForWord(word, this.baseFontSize, this.config);
  }

  /**
   * Calculate ORP position for a word
   *
   * The ORP is typically at ~35% of the word for optimal recognition.
   * We position the word so this point aligns with orpX of the canvas.
   *
   * @param word - The word to position
   * @param fontSize - The font size being used
   * @returns x position for the word start
   */
  private calculateWordX(word: string, fontSize: number): number {
    // Measure the full word width
    this.ctx.font = `${this.config.fontWeight} ${fontSize}px ${this.config.fontFamily}`;
    const metrics = this.ctx.measureText(word);
    const wordWidth = metrics.width;

    // ORP position within the word (typically 35% from start for most words)
    const orpInWord = wordWidth * 0.35;

    // Target x position for ORP on canvas
    const orpCanvasX = this.config.textureWidth * this.config.orpX;

    // Word start position
    const wordX = orpCanvasX - orpInWord;

    return wordX;
  }

  /**
   * Clear the canvas and draw frosted glass background
   */
  clear(): void {
    const w = this.config.textureWidth;
    const h = this.config.textureHeight;
    const r = h * this.config.cornerRadius;

    // Clear to transparent first
    this.ctx.clearRect(0, 0, w, h);

    // Draw frosted glass panel with rounded corners
    this.ctx.beginPath();
    this.ctx.roundRect(0, 0, w, h, r);
    this.ctx.closePath();

    // Fill with glass background
    this.ctx.fillStyle = this.config.glassFill;
    this.ctx.fill();

    // Draw subtle border
    this.ctx.strokeStyle = this.config.glassBorder;
    this.ctx.lineWidth = 4; // 4px border at texture resolution
    this.ctx.stroke();
  }

  /**
   * Render a word to the canvas
   *
   * @param word - The word to render
   * @returns true if canvas was updated, false if word unchanged
   */
  renderWord(word: string): boolean {
    // Skip if same word (optimization for XR texture updates)
    if (word === this.lastWord) {
      return false;
    }
    this.lastWord = word;

    // Clear canvas
    this.clear();

    // Skip empty words
    if (!word || word.trim().length === 0) {
      return true;
    }

    // Calculate font size for this word
    const fontSize = this.getFontSizeForWord(word);

    // Set font
    this.ctx.font = `${this.config.fontWeight} ${fontSize}px ${this.config.fontFamily}`;
    this.ctx.textBaseline = 'middle';
    this.ctx.textAlign = 'left';

    // Calculate position with ORP anchoring
    const x = this.calculateWordX(word, fontSize);
    const y = this.config.textureHeight / 2;

    // Render word with high contrast
    // Using white text for XR visibility (background will be handled by pane material)
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    this.ctx.fillText(word, x, y);

    return true;
  }

  /**
   * Get the canvas element (for creating Three.js texture)
   */
  getCanvas(): HTMLCanvasElement | OffscreenCanvas {
    return this.canvas;
  }

  /**
   * Get canvas dimensions
   */
  getDimensions(): { width: number; height: number } {
    return {
      width: this.config.textureWidth,
      height: this.config.textureHeight,
    };
  }

  /**
   * Get the current configuration
   */
  getConfig(): Readonly<TextCanvasConfig> {
    return { ...this.config };
  }

  /**
   * Force re-render of current word (e.g., after config change)
   */
  forceRender(): void {
    const word = this.lastWord;
    this.lastWord = '';
    this.renderWord(word);
  }
}

/**
 * Create a TextCanvas with configuration from CSS tokens
 */
export function createTextCanvas(): TextCanvas {
  const config = loadConfigFromTokens();
  return new TextCanvas(config);
}
